import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material'
import { FiPlus, FiDownload, FiMail } from 'react-icons/fi'
import { receiptsAPI } from '../services/api'
import { colors } from '../theme/modernTheme'

export default function Receipts() {
  const [receipts, setReceipts] = useState([])

  useEffect(() => {
    loadReceipts()
  }, [])

  const loadReceipts = async () => {
    try {
      const response = await receiptsAPI.getAll({ limit: 50 })
      setReceipts(response.data)
    } catch (error) {
    }
  }

  const handleDownload = async (id) => {
    try {
      const blob = await receiptsAPI.downloadPDF(id)
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `receipt_${id}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
    }
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        mt={3}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}>
          Receipts
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            backgroundColor: colors.primary.main,
            '&:hover': { backgroundColor: colors.primary.dark },
          }}>
          Generate Receipt
        </Button>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "4px",
          boxShadow: 'none',
          border: `1px solid ${colors.border.main}`,
        }}>
        <Table>
          <TableHead sx={{ backgroundColor: colors.background.paper }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                Receipt Number
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                Donor
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                Amount
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                Delivery Method
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receipts.map((receipt) => (
              <TableRow
                key={receipt.id}
                sx={{
                  '&:hover': { backgroundColor: colors.action.hover },
                  '&:last-child td, &:last-child th': { border: 0 },
                  borderBottom: `1px solid ${colors.border.main}`,
                }}>
                <TableCell
                  sx={{ width: "110px", fontSize: '0.875rem', color: colors.text.primary }}>
                  {receipt.receipt_number}
                </TableCell>
                <TableCell
                  sx={{ width: "110px", fontSize: '0.875rem', color: colors.text.primary }}>
                  {new Date(receipt.issue_date).toLocaleDateString()}
                </TableCell>
                <TableCell
                  sx={{
                    maxWidth: "160px",
                    fontSize: '0.875rem',
                    color: colors.text.primary,
                    fontWeight: 500,
                  }}>
                  <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Tooltip title={receipt.first_name
                      ? `${receipt.first_name} ${receipt.last_name || ''}`
                      : receipt.organization_name} arrow placement="top">
                      <span>
                        {receipt.first_name
                          ? `${receipt.first_name} ${receipt.last_name || ''}`
                          : receipt.organization_name}
                      </span>
                    </Tooltip>
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: "100px",
                    fontSize: '0.875rem',
                    color: colors.text.primary,
                    fontWeight: 600,
                  }}>
                  ₹ {parseFloat(receipt.amount).toLocaleString() || 0}
                </TableCell>
                <TableCell
                  sx={{ width: "100px", fontSize: '0.875rem', color: colors.text.secondary }}>
                  {receipt.delivery_method}
                </TableCell>
                <TableCell sx={{ width: "100px" }}>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(receipt.id)}
                      sx={{
                        color: colors.primary.main,
                        '&:hover': { backgroundColor: colors.action.hover },
                      }}>
                      <FiDownload size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        color: colors.text.secondary,
                        '&:hover': { backgroundColor: colors.action.hover },
                      }}>
                      <FiMail size={16} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom Pagination - Note: Add pagination state if needed */}
      {receipts.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 2,
            gap: 1,
          }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              textAlign: 'center',
            }}>
            Showing {receipts.length} receipts
          </Typography>
        </Box>
      )}
    </Box>
  )
}
